/**
 * Hash routes, shaped like AgencyAnalytics': agency areas at the top level,
 * and everything about one client under #/client/<id>/<tab>.
 */

import { useEffect, useState } from 'react';

export const AGENCY_AREAS = [
  'clients',
  'reports',
  'rollups',
  'kpis',
  'data',
  'templates',
  'mcp',
  'settings',
] as const;
export type AgencyArea = (typeof AGENCY_AREAS)[number];

export const CLIENT_TABS = [
  'dashboards',
  'reports',
  'data',
  'users',
  'client-portal',
  'benchmarks',
  'kpis',
  'settings',
] as const;
export type ClientTab = (typeof CLIENT_TABS)[number];

/** Agency areas that open a document at #/<area>/<id> (roll-ups, agency reports). */
const DOCUMENT_AREAS: readonly AgencyArea[] = ['rollups', 'reports'];

export type Route =
  | { area: AgencyArea; documentId?: string | null | undefined }
  | {
      area: 'client';
      clientId: string;
      tab: ClientTab;
      documentId: string | null;
      /** An Explore view under the Dashboards tab, e.g. "channels/all" (GA4). */
      explore?: string | undefined;
    };

/** Explore's address segment per source, as AgencyAnalytics names it. */
export const EXPLORE_SEGMENT = 'google-analytics-4';
const VIEW_SLUG = /^[a-z0-9-]{1,60}$/;

const UUID = /^[0-9a-f-]{36}$/;

export function parseRoute(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  if (parts[0] === 'client' && parts[1] && UUID.test(parts[1])) {
    if (parts[2] === EXPLORE_SEGMENT) {
      const view = parts
        .slice(3)
        .filter((part) => VIEW_SLUG.test(part))
        .join('/');
      return {
        area: 'client',
        clientId: parts[1],
        tab: 'dashboards',
        documentId: null,
        explore: view || 'channels/all',
      };
    }
    const tab = (CLIENT_TABS as readonly string[]).includes(parts[2] ?? '')
      ? (parts[2] as ClientTab)
      : 'dashboards';
    const documentId = parts[3] && UUID.test(parts[3]) ? parts[3] : null;
    return { area: 'client', clientId: parts[1], tab, documentId };
  }
  const area = (AGENCY_AREAS as readonly string[]).includes(parts[0] ?? '')
    ? (parts[0] as AgencyArea)
    : 'clients';
  if (DOCUMENT_AREAS.includes(area) && parts[1] && UUID.test(parts[1]))
    return { area, documentId: parts[1] };
  return { area };
}

export function hrefFor(route: Route): string {
  if (route.area !== 'client')
    return route.documentId ? `#/${route.area}/${route.documentId}` : `#/${route.area}`;
  if (route.explore) return `#/client/${route.clientId}/${EXPLORE_SEGMENT}/${route.explore}`;
  return `#/client/${route.clientId}/${route.tab}${route.documentId ? `/${route.documentId}` : ''}`;
}

export function go(route: Route): void {
  window.location.hash = hrefFor(route);
}

/** The current route, kept in step with the address bar in both directions. */
export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}
